import { Injectable, NgZone } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';

/**
 * One place that knows how much of the screen the software keyboard is eating
 * (correction brief section 5). Before this, every typed screen was at the mercy
 * of the browser's own scroll-into-view: on Android the keyboard takes roughly
 * half the viewport, and the field, its Continue button, or the animation the
 * task depends on could all end up underneath it.
 *
 * The visual viewport is the only thing that actually reports this. `resize` on
 * `window` does not fire for the keyboard on iOS at all, and on Android it
 * reports a layout change without saying what covered it.
 *
 * What it publishes, for anyone to use without wiring listeners again:
 *   --kb-inset   how many CSS pixels the keyboard covers, 0px when closed
 *   body.kb-open while it is open
 *
 * So a sticky action dock only needs `bottom: var(--kb-inset, 0px)` to ride
 * above the keyboard, and a screen that wants a compact composition while
 * typing only needs a `body.kb-open` rule.
 *
 * It also does the two things CSS cannot: bring the focused field (and the
 * action under it) above the keyboard when it opens, and put the page back where
 * it was when it closes.
 */
@Injectable({ providedIn: 'root' })
export class KeyboardInsetService {
  /** below this many pixels the shrink is browser chrome, not a keyboard */
  private static readonly OPEN_THRESHOLD = 120;

  private started = false;
  private open = false;
  private inset = 0;
  /** where it was before the keyboard pushed it, restored on close */
  private scrollBeforeOpen: number | null = null;
  private scrollerBeforeOpen: HTMLElement | Window | null = null;
  private raf = 0;

  constructor(
    private zone: NgZone,
    private router: Router,
  ) {}

  start(): void {
    if (this.started) return;
    if (typeof window === 'undefined' || typeof document === 'undefined') return;
    const vv = window.visualViewport;
    if (!vv) return; // every browser we support has it; older ones keep today's behaviour
    this.started = true;

    // Outside Angular: this fires on every keyboard frame and on every scroll of
    // the visual viewport, and none of it needs change detection.
    this.zone.runOutsideAngular(() => {
      const onChange = () => this.schedule();
      vv.addEventListener('resize', onChange);
      vv.addEventListener('scroll', onChange);
      document.addEventListener('focusin', () => this.onFocusIn(), true);
      this.apply();
    });

    // Leaving a screen with the keyboard up (e.g. typing in the pre-visit form,
    // then Generate) can leave iOS WKWebView with the document scrolled and the
    // focused field alive, so taps on the next screen land off target until the
    // app is reopened (Danna, 13.10, report tabs dead). Drop the focus and put the
    // document back at the top on every navigation; screens scroll their own
    // containers, never the document.
    this.router.events
      .pipe(filter((e) => e instanceof NavigationEnd))
      .subscribe(() => this.resetAfterNavigation());
  }

  private resetAfterNavigation(): void {
    const el = document.activeElement as HTMLElement | null;
    if (el && this.isTypable(el)) el.blur();
    if (window.scrollY !== 0 || window.scrollX !== 0) window.scrollTo(0, 0);
  }

  private schedule(): void {
    if (this.raf) return;
    this.raf = requestAnimationFrame(() => {
      this.raf = 0;
      this.apply();
    });
  }

  private apply(): void {
    const vv = window.visualViewport;
    if (!vv) return;
    // What the layout viewport has that the visual one does not, minus whatever
    // was scrolled away at the top. Rounded down so a fractional pixel from a
    // zoom level never registers as a keyboard.
    const covered = Math.max(
      0,
      Math.round(window.innerHeight - vv.height - vv.offsetTop),
    );
    const open = covered > KeyboardInsetService.OPEN_THRESHOLD;
    const inset = open ? covered : 0;
    if (open === this.open && inset === this.inset) return;

    const wasOpen = this.open;
    this.open = open;
    this.inset = inset;
    document.documentElement.style.setProperty('--kb-inset', inset + 'px');
    document.body.classList.toggle('kb-open', open);

    if (open && !wasOpen) {
      const el = document.activeElement as HTMLElement | null;
      this.scrollerBeforeOpen = this.scrollerOf(el);
      this.scrollBeforeOpen = this.topOf(this.scrollerBeforeOpen);
      this.revealFocused();
    } else if (!open && wasOpen) {
      this.restoreScroll();
    }
  }

  private onFocusIn(): void {
    // Moving between fields while the keyboard is already up does not resize the
    // viewport, so nothing above would fire. Reveal the new field too.
    if (this.open) setTimeout(() => this.revealFocused(), 50);
  }

  /**
   * Put the focused field, and whatever action sits directly under it, above the
   * keyboard. The browser's own scroll-into-view aims for the layout viewport,
   * which still counts the covered strip as visible, so a field just above the
   * keyboard is left half hidden and the button under it is not considered at
   * all.
   */
  private revealFocused(): void {
    const el = document.activeElement as HTMLElement | null;
    if (!el || !this.isTypable(el)) return;
    const vv = window.visualViewport;
    if (!vv) return;
    const rect = el.getBoundingClientRect();
    // the bottom of what is really visible, in client coordinates
    const visibleBottom = vv.offsetTop + vv.height;
    // room for the field plus a little of whatever follows it
    const wanted = rect.bottom + 84;
    const overflow = wanted - visibleBottom;
    const scroller = this.scrollerOf(el);
    if (overflow > 0) {
      this.scrollBy(scroller, overflow);
    } else if (rect.top < vv.offsetTop + 8) {
      this.scrollBy(scroller, rect.top - vv.offsetTop - 8);
    }
  }

  private restoreScroll(): void {
    const y = this.scrollBeforeOpen;
    const scroller = this.scrollerBeforeOpen;
    this.scrollBeforeOpen = null;
    this.scrollerBeforeOpen = null;
    if (y === null || !scroller) return;
    // after the layout settles back to the full height
    setTimeout(() => {
      if (scroller === window) window.scrollTo({ top: y, behavior: 'auto' });
      else (scroller as HTMLElement).scrollTop = y;
    }, 60);
  }

  /**
   * The app does not scroll the document: most screens live inside a container
   * with its own overflow, so scrolling `window` would do nothing at all. Walk up
   * to whatever is actually scrolling this field.
   */
  private scrollerOf(el: HTMLElement | null): HTMLElement | Window {
    let node = el?.parentElement ?? null;
    while (node && node !== document.body) {
      const style = getComputedStyle(node);
      const scrollable =
        /(auto|scroll|overlay)/.test(style.overflowY) &&
        node.scrollHeight > node.clientHeight + 1;
      if (scrollable) return node;
      node = node.parentElement;
    }
    return window;
  }

  private topOf(scroller: HTMLElement | Window): number {
    return scroller === window
      ? window.scrollY
      : (scroller as HTMLElement).scrollTop;
  }

  private scrollBy(scroller: HTMLElement | Window, delta: number): void {
    if (scroller === window) window.scrollBy({ top: delta, behavior: 'smooth' });
    else
      (scroller as HTMLElement).scrollBy({ top: delta, behavior: 'smooth' });
  }

  private isTypable(el: HTMLElement): boolean {
    const tag = el.tagName;
    if (tag === 'TEXTAREA') return true;
    if (el.isContentEditable) return true;
    if (tag !== 'INPUT') return false;
    const type = (el as HTMLInputElement).type;
    return !['checkbox', 'radio', 'button', 'submit', 'range', 'file'].includes(
      type,
    );
  }
}
